import { StateNodeConfig } from 'xstate';
import { Model } from 'xstate/lib/model.types';
import { BaseContext, BaseEvent } from '.';
import { Components, Input } from '../components';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StateData = StateNodeConfig<BaseContext, any, BaseEvent>;

export interface CustomElementData {
  id: string;
  jsx: JSX.Element;
  nextState?: string;
}

export type CustomElement = (data: CustomElementData) => StateData;

export interface RemoteMessageData {
  id: string;
  message?: string | JSX.Element;
  nextState?: string;
  // Special attribute for success message. It tells which state input to use for showing success message.
  parent?: string;
}

export type RemoteMessage = (data: RemoteMessageData) => StateData;

export interface RemoteInputData extends RemoteMessageData {
  component: Components;
  componentInputs?: Input[];
  failState?: string;
}

export type RemoteInput = (
  model: Model<BaseContext, BaseEvent, void>,
  data: RemoteInputData,
  actions?: string[]
) => StateData;

/**
 * Create a state that renders a custom element.
 * @param data
 * @returns
 */
export const customElement: CustomElement = (data) => {
  const output: StateData = {
    id: data.id,
    tags: ['custom_element'],
    meta: {
      jsx: data.jsx,
    },
  };

  if (data.nextState) {
    output.after = {
      1000: data.nextState,
    };
  } else {
    output.type = 'final';
  }
  return output;
};

/**
 * Create a state that renders a remote message
 * @param data
 * @returns
 */
export const remoteMessage: RemoteMessage = (data) => {
  const output: StateData = {
    id: data.id,
    tags: ['remote_input'],
    meta: {
      message: data.message,
    },
  };

  if (data.nextState) {
    output.after = {
      1000: data.nextState,
    };
  } else {
    output.type = 'final';
  }
  return output;
};

/**
 * Create a state that renders a success message.
 * @param data
 * @returns
 */
export const successMessage: RemoteMessage = (data) => {
  const output: StateData = {
    id: data.id,
    tags: ['success_message'],
    meta: {
      message: data.message,
      parent: data.parent,
    },
  };

  if (data.nextState) {
    output.after = {
      1000: data.nextState,
    };
  } else {
    output.type = 'final';
  }
  return output;
};

export const remoteInput: RemoteInput = (model, data, actions) => {
  const output: StateData = {
    id: data.id,
    tags: ['remote_input'],
    meta: {
      message: data.message,
    },
    entry: model.assign({
      input_state: data.id,
      component: data.component,
      componentInputs: data.componentInputs,
    }),
    exit: model.assign({
      component: undefined, // Important to prevent the same component from rendering again.
      componentInputs: [],
      responses: (context, event) => ({
        ...context.responses,
        [data.id]: event,
      }),
    }),
  };

  if (data.nextState) {
    output.on = {
      SUCCESS: {
        target: data.nextState,
        actions: actions,
      },
    };
  } else {
    output.type = 'final';
  }

  return output;
};
